import { Box, Stack, Typography } from '@mui/material';
import { outcomeColor } from '../lib/outcomeColors';
import { formatTokens } from '../lib/format';
import type { Market } from '../schemas/market.schema';
import { colors, mono } from '../themes/theme';

interface PoolBarProps {
    market: Market;
    /** Hidden on a card where space is tight. */
    showLegend?: boolean;
}

/**
 * One bar showing how the pot is split.
 *
 * Each outcome used to get its own bar, which read as a row of separate graphs
 * rather than one pot divided up -- and on a two-way market it looked like two
 * unrelated charts instead of a single question. One segmented bar says the
 * thing it is actually trying to say.
 */
export function PoolBar({ market, showLegend = true }: PoolBarProps) {
    const outcomes = market.outcomes;
    const total = outcomes.length;
    const anyStake = market.pot > 0;

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    height: 10,
                    borderRadius: '2px',
                    overflow: 'hidden',
                    bgcolor: colors.track,
                }}
            >
                {anyStake
                    ? outcomes.map((outcome, index) => (
                          <Box
                              key={outcome.id}
                              sx={{
                                  width: `${outcome.share * 100}%`,
                                  bgcolor: outcomeColor(index, total),
                                  transition: 'width 400ms ease',
                              }}
                          />
                      ))
                    : null}
            </Box>

            {!anyStake && (
                <Typography
                    sx={{
                        fontFamily: mono,
                        fontSize: '0.68rem',
                        color: colors.muted,
                        mt: 0.5,
                    }}
                >
                    Nothing staked yet — first bet sets the odds
                </Typography>
            )}

            {showLegend && anyStake && (
                <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 0.75 }}>
                    {outcomes
                        .filter((outcome) => outcome.staked > 0)
                        .map((outcome) => {
                            const index = outcomes.indexOf(outcome);
                            return (
                                <Stack
                                    key={outcome.id}
                                    direction="row"
                                    alignItems="center"
                                    gap={0.5}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '1px',
                                            bgcolor: outcomeColor(index, total),
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: mono,
                                            fontSize: '0.68rem',
                                            color: colors.inkSoft,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {outcome.label} {formatTokens(outcome.staked)}
                                        {outcome.myStake > 0 && ' ·you'}
                                    </Typography>
                                </Stack>
                            );
                        })}
                </Stack>
            )}
        </Box>
    );
}
